import { DefaultButton, Dialog, SpinnerSize, Stack } from '@fluentui/react';
import React, { useEffect } from 'react';
import styles from './styles.module.scss';

// assets

import Label from '../../../typography/label';
import Text from '../../../typography/text';
import LoadingComponent from '../../../feedbacks/loading';
import { TFileType } from '../../../../manifests/fileFormats';
import { IDocumentProps, } from '../../../../props/general';
import moment from 'moment';
import DocumentService from '../../../../services/documents/document';
import FileService from '../../../../services/general/file';
import { DocumentTypeProps } from '../uploads/panel';
import DocumentTypes from '../../../../manifests/documentTypes';
import Details from '../../../typography/details';
import DocumentVisibilities from '../../../../manifests/documentVisibilities';
import GeneralService from '../../../../services/general';


export type TTagVariant = 'default' | 'active' | 'success' | 'light' | 'error' | 'warning' | 'transparent';

const VIDEO_EXTENSIONS: Record<string, string> = {
    mp4: 'video/mp4',
    webm: 'video/webm',
    ogg: 'video/ogg',
    mov: 'video/quicktime',
    mkv: 'video/x-matroska',
    m4v: 'video/mp4',
};


const base64ToBlobUrl = (base64Str: string, contentType: string = 'application/pdf'): string => {
    try {
        const byteCharacters = atob(base64Str);
        const byteArrays = [];
        const sliceSize = 512;

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });
        return URL.createObjectURL(blob);
    } catch (e) {
        console.error("Error creating Blob URL for PDF:", e);
        return `data:${contentType};base64,${base64Str}`;
    }
};

type FilePreviewProps = {
    title?: string;
    file: IDocumentProps;
    metadata?: any;
    documentTypes?: DocumentTypeProps[];
    onDismissed(): void;
}

const FilePreview: React.FC<FilePreviewProps> = (props: FilePreviewProps) => {
    const [file, setFile] = React.useState<IDocumentProps>(props.file);
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [images, setImages] = React.useState<string[]>([]);
    const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
    const [videoUrl, setVideoUrl] = React.useState<string | null>(null);
    const [downloading, setDownloading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const activeBlobUrls = React.useRef<string[]>([]);

    const cleanupBlobUrls = () => {
        activeBlobUrls.current.forEach(url => {
            if (url && url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        });
        activeBlobUrls.current = [];
    };

    useEffect(() => {
        init(props.file);
        return () => {
            cleanupBlobUrls();
        };
    }, [props.file]);

    const init = async (file: IDocumentProps) => {
        try {
            cleanupBlobUrls();
            setLoaded(false);
            setError(null);
            setPdfUrl(null);
            setVideoUrl(null);
            setImages([]);

            const base64 = (await DocumentService.get(file.path)).data.data;
            const ext = (file.basename || file.path).split(".").pop()?.toLowerCase() || "";

            if (ext === 'pdf') {
                const url = base64ToBlobUrl(base64, 'application/pdf');
                activeBlobUrls.current.push(url);
                setPdfUrl(url);
            } else if (VIDEO_EXTENSIONS[ext]) {
                const mime = VIDEO_EXTENSIONS[ext];
                const url = base64ToBlobUrl(base64, mime);
                activeBlobUrls.current.push(url);
                setVideoUrl(url);
            } else {
                const images = await FileService.getImageBase64(base64, ext);
                setImages(images);
            }
        } catch (err: any) {
            console.error("Failed to load file preview:", err);
            setError(err?.message || "Gagal memuat pratinjau file.");
        } finally {
            setLoaded(true);
        }
    }

    const _onDownload = async () => {
        try {
            setDownloading(true);
            await FileService.download(file);
            setDownloading(false);
        } catch (error) {
            setDownloading(false);
            console.log(error);
        }
    }

    return <Dialog hidden={false}
        dialogContentProps={{ title: props.title || file.basename || file.path.split("/").pop() }}
        onDismiss={props.onDismissed}
        modalProps={{ isBlocking: true }}
        styles={{main: {maxWidth: "1024px !important"}}}>
        <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack grow={1} className={styles.preview}>
                {!loaded ? <LoadingComponent label={'Generating file preview ...'} size={SpinnerSize.medium} /> : null}
                {loaded && error ? <Text style={{ color: '#a80000', fontStyle: 'italic', padding: 20 }}>{error}</Text> : null}
                {loaded && !error && pdfUrl ? (
                    <iframe
                        src={pdfUrl}
                        title={file.basename}
                        style={{ width: '100%', minWidth: '600px', height: '600px', border: 'none', borderRadius: '4px' }}
                    />
                ) : null}
                {loaded && !error && videoUrl ? (
                    <video
                        src={videoUrl}
                        controls
                        playsInline
                        className={styles.video}
                    >
                        Browser Anda tidak mendukung pratinjau video langsung.
                    </video>
                ) : null}
                {loaded && !error && !pdfUrl && !videoUrl && images.length > 0 ? images.map((preview, idx) => {
                    return <img key={idx} src={preview} className={styles.image} alt={file.basename} />;
                }) : null}
                {loaded && !error && !pdfUrl && !videoUrl && images.length === 0 ? <Text style={{ fontStyle: 'italic', padding: 20 }}>Pratinjau tidak tersedia untuk format file ini.</Text> : null}
            </Stack>
            <Stack className={styles.properties} tokens={{childrenGap: 15}}>
                <Stack.Item>
                    {!downloading ? <DefaultButton iconProps={{iconName: "Download"}}
                        text={"Download"}
                        label={"Download"}
                        onClick={() => _onDownload()} /> : null}
                    {downloading ? <LoadingComponent spinnerPosition={'baseline'} label={'Downloading ...'} labelPosition={'right'} /> : null}
                </Stack.Item>
                <Stack className={'divider'}></Stack>
                <Details title={'File name'} text={file.basename} />
                <Details title={'Type'} text={(props.documentTypes || DocumentTypes).find((t) => t.key === file.type)?.text || "-"} />
                <Stack className={'divider'}></Stack>
                <Details title={'Size'} text={file.size ? GeneralService.getFriendlyFileSizeText(file.size) : "-"} />
                <Details title={'Visibility'} text={(DocumentVisibilities).find((t) => t.key === file.visibility)?.text || "Private"} />
                <Details title={'Uploaded at'} text={moment(file.uploadedAt).format("DD/MM/YYYY HH:mm")} />
            </Stack>
        </Stack>
    </Dialog>;
};

export default FilePreview;
